/* eslint-disable react-refresh/only-export-components */
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    ReactNode,
} from 'react';
import { rememberConsumedEventIds } from '../domain/bridgeSumator';
import { createSumatorLineId } from '../domain/sumator';

export interface SumatorLine {
    id: string;
    description: string;
    amount: number;
}

interface SumatorContextType {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
    lines: SumatorLine[];
    addLine: (description: string, amount: number) => void;
    /**
     * Appends a line produced by a legacy operation replayed through the
     * bridge, at most once per bridge event. Answers whether it was appended.
     */
    addBridgeLine: (eventId: string, description: string, amount: number) => boolean;
    removeLine: (id: string) => void;
    clearSumator: () => void;
    primit: number | null;
    setPrimit: (amount: number | null) => void;
    multiClient: boolean;
    setMultiClient: (multi: boolean) => void;
}

const SumatorContext = createContext<SumatorContextType | undefined>(undefined);

export const useSumator = () => {
    const context = useContext(SumatorContext);
    if (!context) {
        throw new Error('useSumator must be used within a SumatorProvider');
    }
    return context;
};

export const SumatorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [lines, setLines] = useState<SumatorLine[]>([]);
    const [primit, setPrimit] = useState<number | null>(null);
    const [multiClient, setMultiClient] = useState(false);

    /**
     * Bridge events already appended. A ref rather than state: it must not
     * re-render anything, and it deliberately survives `clearSumator` - clearing
     * the widget is not an instruction to replay the last minute of legacy
     * operations back into it on the next poll.
     */
    const consumedBridgeEventIds = useRef<string[]>([]);

    const addLine = useCallback((description: string, amount: number) => {
        setLines(prev => [...prev, { id: createSumatorLineId(), description, amount }]);
        setIsOpen(true);
        setCollapsed(false);
    }, []);

    const addBridgeLine = useCallback(
        (eventId: string, description: string, amount: number) => {
            // The poll re-reads a window behind its own cursor so a late-applied
            // event cannot be missed, which means the same line legitimately
            // arrives more than once. This is where that is absorbed.
            if (consumedBridgeEventIds.current.includes(eventId)) {
                return false;
            }
            consumedBridgeEventIds.current = rememberConsumedEventIds(
                consumedBridgeEventIds.current,
                [eventId],
            );
            addLine(description, amount);
            return true;
        },
        [addLine],
    );

    const removeLine = useCallback((id: string) => {
        setLines(prev => prev.filter(l => l.id !== id));
    }, []);

    const clearSumator = useCallback(() => {
        setLines([]);
        setPrimit(null);
    }, []);

    const value = useMemo(
        () => ({
            isOpen,
            setIsOpen,
            collapsed,
            setCollapsed,
            lines,
            addLine,
            addBridgeLine,
            removeLine,
            clearSumator,
            primit,
            setPrimit,
            multiClient,
            setMultiClient,
        }),
        [
            isOpen,
            collapsed,
            lines,
            addLine,
            addBridgeLine,
            removeLine,
            clearSumator,
            primit,
            multiClient,
        ],
    );

    return (
        <SumatorContext.Provider value={value}>
            {children}
        </SumatorContext.Provider>
    );
};
